<template>
    <div
        :id="id"
        :class="classString"
    >
        <div class="--row lni-u-gap2">
            <div
                class="lni-c-box--content --card"
                :class="{ '--col__2of4': isManagementReport || isErgonomicEvaluation }"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.handWristText }}
                </p>
                <span class="--label">
                    {{ cusiText }}
                </span>
            </div>
            <div
                v-if="isErgonomicEvaluation"
                class="lni-c-box--content --card --col__2of4"
            >
                <div class="lni-u-flex lni-u-mb1">
                    <p class="lni-u-type--xs lni-u-line-height--tight">
                        {{ content?.cusiHeading }}
                    </p>
                    <lni-janitorial-info-modal
                        :id="`${id}_cusi-info`"
                        classString="lni-u-flex"
                        :title="content?.cusiHeading"
                        :body="content?.cusiMessage"
                    />
                </div>
                <span class="--label --number">
                    {{ cumulativeStrainIndex }}
                </span>
            </div>
        </div>
        <template v-if="isManagementReport || isErgonomicEvaluation">
            <lni-janitorial-complaint-card
                :id="`${id}_complaint`"
                classString="lni-u-mt2"
                :complaint="complaint"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.complaintPrefix }}
                </p>
                <span class="--label">
                    {{ complaint ? '' : content?.notText }} {{ content?.handWristComplaintText }}
                </span>
            </lni-janitorial-complaint-card>
            <lni-janitorial-discomfort-level
                v-if="isErgonomicEvaluation"
                :id="`${id}_discomfort`"
                classString="lni-u-mt2"
                :name="content?.handWristLabel"
                :shiftStartDiscomfort="shiftStartDiscomfort"
                :shiftEndDiscomfort="shiftEndDiscomfort"
            />
        </template>
        <h3 class="lni-u-heading--3 lni-u-mt3">
            {{ content?.handWristCalculationHeading }}
        </h3>
        <div class="lni-u-reading-width--max lni-u-mt1">
            <p v-if="isErgonomicEvaluation">
                {{ content?.handWristTableErgonomicDescription }}
            </p>
            <p v-else>
                {{ content?.handWristTableDescription }}
            </p>
        </div>
        <lni-table
            v-if="isErgonomicEvaluation"
            :id="`${id}_table`"
            class="lni-u-mb3"
            :hidePagingActions="true"
            :caption="content?.handWristTableCaption"
            :summary="content?.handWristTableSummary"
            :striped="true"
            :items="items"
            :noRowsMessage="content?.handWristTableNoRowsMessage"
            loadState="loaded"
            :columns="columns"
            :sort="{
                rsi: 'desc',
            }"
        >
            <template
                slot="body"
                slot-scope="props"
            >
                <tr>
                    <td class="lni-u-text--center">
                        {{ props.item.index }}
                    </td>
                    <td class="lni-u-text--left">
                        {{ props.item.assignment }}
                    </td>
                    <td class="lni-u-text--center">
                        {{ props.item.subtask }}
                    </td>
                    <td class="lni-u-text--center">
                        {{ props.item.rsi }}
                    </td>
                </tr>
            </template>
        </lni-table>
        <lni-table
            v-else
            :id="`${id}_table`"
            class="lni-u-mb3"
            :hidePagingActions="true"
            :caption="content?.handWristTableCaption"
            :summary="content?.handWristTableSummary"
            :striped="true"
            :items="items"
            :noRowsMessage="content?.handWristTableNoRowsMessage"
            loadState="loaded"
            :columns="columns"
            :sort="{
                rsi: 'desc',
            }"
        >
            <template
                slot="body"
                slot-scope="props"
            >
                <tr>
                    <td class="lni-u-text--center">
                        {{ props.item.index }}
                    </td>
                    <td class="lni-u-text--left">
                        {{ props.item.assignment }}
                    </td>
                    <td class="lni-u-text--center">
                        {{ props.item.subtask }}
                    </td>
                </tr>
            </template>
        </lni-table>
    </div>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import {
    cumulativeStrainIndex,
    revisedStrainIndex,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    assignmentText,
    toFixedOne,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

import LniJanitorialComplaintCard from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-complaint-card.component.vue';
import LniJanitorialDiscomfortLevel from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-discomfort-level.component.vue';
import LniJanitorialInfoModal from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-info-modal.component.vue';

export default {
    name: 'LniJanitorialRiskHandWrist',
    components: {
        LniJanitorialComplaintCard,
        LniJanitorialDiscomfortLevel,
        LniJanitorialInfoModal,
    },
    props: {
        classString: {
            type: String,
            default: '',
        },
        complaint: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            required: true,
        },
        jobPlan: {
            type: Array,
            required: true,
        },
        shiftEndDiscomfort: {
            type: Number,
            default: -1,
        },
        shiftStartDiscomfort: {
            type: Number,
            default: -1,
        },
    },
    data() {
        return {
            rsiLimit: 3, // null to turn off and display all subtask RSI
        };
    },
    computed: {
        columns() {
            const result = [
                {
                    source: 'subtask',
                    displayText: 'Subtask',
                    headerClasses: 'lni-u-text--left',
                },
            ];

            if (this.isErgonomicEvaluation) {
                result.push(
                    {
                        source: 'rsi',
                        displayText: this.content?.rsiText,
                    }
                );
            }

            return this.tableColumns(result);
        },
        cumulativeStrainIndex() {
            return toFixedOne(cumulativeStrainIndex(this.jobPlan));
        },
        cusiText() {
            const min = 7.5;
            const max = 15;
            let result = this.content?.moderateText;
            if (this.cumulativeStrainIndex >= max) {
                result = this.content?.highText;
            }
            if (this.cumulativeStrainIndex < min) {
                result = this.content?.lowText;
            }
            return result;
        },
        items() {
            let result = [];

            this.jobPlan.forEach(assignment => {
                assignment.subtasks.forEach(subtask => {
                    const rsi = revisedStrainIndex(assignment, subtask);
                    if (!this.rsiLimit || (this.rsiLimit && Number(rsi) > this.rsiLimit)) {
                        let data = {
                            assignment: assignmentText(assignment),
                            subtask: subtask.title,
                        };

                        if (this.isErgonomicEvaluation) {
                            data.rsi = toFixedOne(rsi);
                        }

                        result.push(data);
                    }
                });
            });

            if (this.isErgonomicEvaluation) {
                result = result.sort((a, b) => b.rsi - a.rsi);
            }

            return result.map((assignment, index) => ({
                ...assignment,
                index: index + 1,
            }));
        },
        ...mapGetters({
            content: 'appState/content',
            isErgonomicEvaluation: 'appState/isErgonomicEvaluation',
            isManagementReport: 'appState/isManagementReport',
            tableColumns: 'appState/tableColumns',
        }),
    },
    mounted() {
        this.$nextTick(() => {
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'items',
                value: this.items,
            }, {
                root: true,
            });
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'totalItems',
                value: this.items.length,
            }, {
                root: true,
            });
        });
    },
};
</script>
